import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { 
  Upload, 
  X, 
  Send, 
  AlertCircle, 
  Loader2,
  Info
} from 'lucide-react'
import apiService from '../../services/api'

interface ProposalSubmissionFormProps {
  rfqId: string
  rfqTitle: string
  agencyName: string
  deadline: string
  onSuccess?: () => void
  onCancel?: () => void
}

interface ProposalFormData {
  message: string
  attachments: File[]
}

export default function ProposalSubmissionForm({
  rfqId,
  rfqTitle,
  agencyName,
  deadline,
  onSuccess,
  onCancel
}: ProposalSubmissionFormProps) {
  const [formData, setFormData] = useState<ProposalFormData>({
    message: '',
    attachments: []
  })
  const [dragActive, setDragActive] = useState(false)
  const queryClient = useQueryClient()

  const submitProposalMutation = useMutation({
    mutationFn: async (data: ProposalFormData) => {
      const formDataToSend = new FormData()
      formDataToSend.append('message', data.message)
      
      // Add file attachments
      data.attachments.forEach((file) => {
        formDataToSend.append('attachments', file)
      })

      const response = await apiService.post(`/gov/proposals/rfq/${rfqId}/respond`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      if (typeof response === 'object' && response && 'data' in response) {
        return (response as any).data;
      }
      return undefined;
    },
    onSuccess: () => {
      toast.success('Proposal submitted successfully!')
      queryClient.invalidateQueries({ queryKey: ['vendor-proposals'] })
      onSuccess?.()
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to submit proposal'
      toast.error(message)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.message.trim()) {
      toast.error('Please enter a proposal message')
      return
    }

    if (formData.message.length < 10) {
      toast.error('Proposal message must be at least 10 characters')
      return
    }

    submitProposalMutation.mutate(formData)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    const files = Array.from(e.dataTransfer.files)
    handleFiles(files)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    handleFiles(files)
  }

  const handleFiles = (files: File[]) => {
    // Filter allowed file types
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ]

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported file type`)
        return false
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(`${file.name} is too large (max 10MB)`)
        return false
      }
      return true
    })

    if (formData.attachments.length + validFiles.length > 5) {
      toast.error('Maximum 5 files allowed')
      return
    }

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }))
  }

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄'
    if (type.includes('word')) return '📝'
    if (type.includes('excel') || type.includes('sheet')) return '📊'
    if (type.includes('image')) return '🖼️'
    return '📎'
  }

  const deadlineDate = new Date(deadline)
  const isDeadlineSoon = deadlineDate.getTime() - Date.now() < 24 * 60 * 60 * 1000 // Less than 24 hours
  const isDeadlinePassed = deadlineDate < new Date()

  if (isDeadlinePassed) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 text-red-700">
          <AlertCircle className="w-6 h-6" />
          <div>
            <h3 className="font-semibold">Deadline Passed</h3>
            <p className="text-sm">This RFQ deadline has passed and is no longer accepting proposals.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Submit Proposal</h2>
        <div className="text-sm text-gray-600">
          <p><strong>RFQ:</strong> {rfqTitle}</p>
          <p><strong>Agency:</strong> {agencyName}</p>
          <p className={`${isDeadlineSoon ? 'text-red-600 font-medium' : ''}`}>
            <strong>Deadline:</strong> {deadlineDate.toLocaleDateString()} at {deadlineDate.toLocaleTimeString()}
            {isDeadlineSoon && <span className="ml-2 text-red-600">⚠️ Less than 24 hours remaining!</span>}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {/* Proposal Message */}
        <div className="mb-6">
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Proposal Message *
          </label>
          <div className="relative">
            <textarea
              id="message"
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Describe your proposal, capabilities, approach, and why you're the best choice for this project..."
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              maxLength={5000}
              required
            />
            <div className="absolute bottom-2 right-2 text-xs text-gray-500">
              {formData.message.length}/5000
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-600">
            Minimum 10 characters. Include your experience, approach, timeline, and any relevant certifications.
          </p>
        </div>

        {/* File Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supporting Documents (Optional)
          </label>
          
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-2">
              Drag & drop files here, or{' '}
              <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                browse
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                />
              </label>
            </p>
            <p className="text-xs text-gray-500">
              PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG • Max 10MB each • Up to 5 files
            </p>
          </div>

          {/* File List */}
          {formData.attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Attached Files ({formData.attachments.length}/5)</h4>
              {formData.attachments.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{getFileIcon(file.type)}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Important Notes */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Important Notes:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Your proposal will be visible only to the requesting agency</li>
                <li>You can update your proposal until the deadline</li>
                <li>Only one proposal per RFQ is allowed</li>
                <li>Make sure to include all relevant information and documentation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              disabled={submitProposalMutation.isPending}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitProposalMutation.isPending || !formData.message.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {submitProposalMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Proposal
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
} 